import * as React from "react"
import { Button } from "../../ui/button"
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "../../ui/tooltip"
import { Spinner } from "../../ui/spinner"
import { useWindowDimensions } from "../../../../../libs/hooksLib/hooksLib"
import { cn } from "../../../lib/utils"

export interface ImageButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
	/**
	 * Image source URL
	 */
	src: string

	/**
	 * Alt text for the image (required for accessibility)
	 */
	alt: string

	/**
	 * Tooltip label to show on hover (desktop) or below button (mobile)
	 */
	tooltipLabel?: string

	/**
	 * Whether the button is in a loading state
	 */
	isLoading?: boolean

	/**
	 * Show label on small screens instead of tooltip
	 * @default true
	 */
	labelOnSmallScreens?: boolean

	/**
	 * Always show as label instead of tooltip
	 * @default false
	 */
	alwaysShowAsLabel?: boolean

	/**
	 * Custom class name for the image
	 */
	imageClassName?: string
}

/**
 * ImageButton is a specialized button component that displays an image as its content.
 * It adapts between tooltip (desktop) and label (mobile) displays based on screen size.
 *
 * Features:
 * - Image-based button content
 * - Responsive tooltip/label behavior
 * - Loading state with spinner
 * - Accessible with alt text
 * - Configurable image size
 *
 * @example Basic usage
 * ```tsx
 * <ImageButton
 *   src="/icons/edit.svg"
 *   alt="Edit"
 *   tooltipLabel="Edit item"
 *   onClick={handleEdit}
 * />
 * ```
 *
 * @example With loading state
 * ```tsx
 * <ImageButton
 *   src="/icons/save.svg"
 *   alt="Save"
 *   tooltipLabel="Save changes"
 *   isLoading={saving}
 *   onClick={handleSave}
 * />
 * ```
 *
 * @example Always show as label
 * ```tsx
 * <ImageButton
 *   src="/icons/download.svg"
 *   alt="Download"
 *   tooltipLabel="Download file"
 *   alwaysShowAsLabel
 *   onClick={handleDownload}
 * />
 * ```
 */
export const ImageButton = React.forwardRef<HTMLButtonElement, ImageButtonProps>(
	(
		{
			src,
			alt,
			tooltipLabel,
			isLoading = false,
			disabled,
			labelOnSmallScreens = true,
			alwaysShowAsLabel = false,
			imageClassName,
			className,
			...props
		},
		ref
	) => {
		const { width } = useWindowDimensions({ includeHeight: false })
		const showAsTooltip = width > 768 && !alwaysShowAsLabel

		const button = (
			<Button
				ref={ref}
				variant="ghost"
				size="icon"
				disabled={disabled || isLoading}
				className={cn("relative cursor-pointer", className)}
				aria-label={alt}
				{...props}>
				{isLoading ? (
					<Spinner aria-label="Loading" />
				) : (
					<img src={src} alt={alt} className={cn("h-6 w-6 object-contain", imageClassName)} />
				)}
			</Button>
		)

		// No tooltip/label needed
		if (!tooltipLabel || !labelOnSmallScreens) {
			return button
		}

		// Show as tooltip on desktop
		if (showAsTooltip) {
			return (
				<TooltipProvider>
					<Tooltip>
						<TooltipTrigger asChild>{button}</TooltipTrigger>
						<TooltipContent>
							<p>{tooltipLabel}</p>
						</TooltipContent>
					</Tooltip>
				</TooltipProvider>
			)
		}

		// Show as label on mobile
		return (
			<div className="flex flex-col items-center gap-1">
				{button}
				<span className="text-xs text-muted-foreground">{tooltipLabel}</span>
			</div>
		)
	}
)

ImageButton.displayName = "ImageButton"
